// Updated LoginForm to fix device ID and login issues

import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack5';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import {
  Link,
  Stack,
  Alert,
  Divider,
  Checkbox,
  TextField,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Typography,
  Button, Box
} from '@material-ui/core';
import { v4 as uuidv4 } from 'uuid';
// import Cookies from "js-cookie";
import { Person, Lock } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Cookies from 'js-cookie';
import { PATH_AUTH } from '../../../routes/paths';
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import { MIconButton } from '../../@material-extend';
import TwoStepVerification from './TwoStepVerification';

// host name
const noEmailHostNameAllowed = ['shrss.beyondqa.ai', 'test.beyondqa.io', 'ai.beyondqa.io', 'localhost', 'tennischannel.beyondqa.ai', 'contactpoint360.beyondqa.ai']

export default function LoginForm() {
  const {
    login,
    enableTwoStep, setEnableTwoStep,
    verifyMfaOtp,
    verifyMfaBackupCode,

    mfaRequired, setMfaRequired
  } = useAuth();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const [enterOtp, setEnterOtp] = useState('');
  const [email, setEmail] = useState('');
  const [deviceChecked, setDeviceChecked] = useState(false);
  const [isEnteredFullOtp, setIsEnteredFullOtp] = useState(false);
  const [useBackupCodeMode, setUseBackupCodeMode] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [backupCodeError, setBackupCodeError] = useState('');
  const [otpError, setOtpError] = useState('');

  const navigate = useNavigate();

  const handleCheckedTrust = (checked) => {

    setDeviceChecked(checked);
  }
  const LoginSchema = Yup.object().shape({
    email: noEmailHostNameAllowed?.includes(window.location.hostname) ? Yup.string().required('*Provide Email/External Id') : Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string().required('Password is required')

  });
  const devices = Cookies.get(`device_ids_${window?.location?.hostname}`) ? JSON.parse(Cookies.get(`device_ids_${window?.location?.hostname}`)) : {};

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      remember: true
    },
    validationSchema: LoginSchema,
    onSubmit: async (values, { setErrors, setSubmitting, resetForm }) => {
      try {
        const encodedEmail = btoa(values.email.toLowerCase());
        const deviceId = devices[encodedEmail] || null;
        // Check if OTP previously failed
        const otpFailed = Cookies.get(`otp_failed_${encodedEmail}`) ? JSON.parse(Cookies.get(`otp_failed_${encodedEmail}`)) : false;

        if (!enableTwoStep && deviceId && !otpFailed) {  // CASE1- USED WHEN DEVICE HAS BEEN TRUSTED
          // await login(values.email.toLowerCase(), values.password, deviceId);
          const data = await login(values.email.toLowerCase(), values.password, deviceId, enableTwoStep, enterOtp, deviceChecked);
          enqueueSnackbar(data?.message, {
            variant: 'success', action: (key) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                <Icon icon={closeFill} />
              </MIconButton>
            )
          });

          // Clear on any OTP failure flag
          Cookies.remove(`otp_failed_${encodedEmail}`);
        } else if (mfaRequired) {
          if (useBackupCodeMode) {
            await verifyMfaBackupCode(values.email.toLowerCase(), backupCode);
          } else {
            await verifyMfaOtp(values.email.toLowerCase(), enterOtp);
          }
          enqueueSnackbar('User Logged in Successfully', {
            variant: 'success', action: (key) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                <Icon icon={closeFill} />
              </MIconButton>
            )
          });
          Cookies.remove(`otp_failed_${encodedEmail}`);
        } else if (enableTwoStep) {
          // CASE-2 TO FILL OTP ONCE LOGIN CRDENTIALS ARE CORRECT (email OTP)
          await login(values.email.toLowerCase(), values.password, deviceChecked ? deviceId : null, enableTwoStep, enterOtp, deviceChecked);
          enqueueSnackbar('User Logged in Successfully', {
            variant: 'success', action: (key) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                <Icon icon={closeFill} />
                9 0/
              </MIconButton>
            )
          });
          Cookies.remove(`otp_failed_${encodedEmail}`);
        } else {
          // checking this for external id
          const isEmail = RegExp(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i).test(values.email.toLowerCase());
          const loginResponse = await login(isEmail ? values.email.toLowerCase() : values.email, values.password, deviceId, enableTwoStep, enterOtp, deviceChecked);

          if (loginResponse && (loginResponse.mfa_required === true || loginResponse.mfaRequired === true)) {
            // MFA authenticator code required — JWTContext already set enableTwoStep + mfaRequired
            enqueueSnackbar('Enter your 6-digit MFA code from your authenticator app', {
              variant: 'info', action: (key) => (
                <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                  <Icon icon={closeFill} />
                </MIconButton>
              )
            });
            return;
          }

          // Only show email OTP page when the backend actually sent an OTP
          if (loginResponse?.is_otp_sent === true || loginResponse?.isOtpSent === true) {
            // Mark that OTP is in-flight so next submit goes through verify_otp
            Cookies.set(`otp_failed_${encodedEmail}`, 'true', { expires: 1 / 48 });
            setEnableTwoStep(true);
            enqueueSnackbar('OTP sent to your email', {
              variant: 'info', action: (key) => (
                <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                  <Icon icon={closeFill} />
                </MIconButton>
              )
            });
          } else {
            // Login succeeded directly (no OTP, no MFA)
            Cookies.remove(`otp_failed_${encodedEmail}`);
            setEnableTwoStep(false);
            enqueueSnackbar('User Logged in Successfully', {
              variant: 'success', action: (key) => (
                <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                  <Icon icon={closeFill} />
                </MIconButton>
              )
            });
          }
        }

        if (isMountedRef.current) {
          setSubmitting(false);
        }
      } catch (error) {
        console.error('Login error:', error);
        const errorMsg = error?.response?.data?.error || error?.response?.data?.message || 'Something went wrong';
        // Handle OTP failure - Force OTP next time
        if (enableTwoStep && !useBackupCodeMode) {
          Cookies.set(`otp_failed_${btoa(values.email.toLowerCase())}`, 'true', { expires: 1 / 48 }); // Ensure OTP next time
        }
        if (useBackupCodeMode) {
          setBackupCodeError(errorMsg);
        } else if (enableTwoStep) {
          setOtpError(errorMsg);
        } else {
          enqueueSnackbar(errorMsg, {
            variant: 'error', action: (key) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                <Icon icon={closeFill} />
              </MIconButton>
            )
          });
          if (isMountedRef.current) {
            setErrors({ afterSubmit: errorMsg });
          }
        }
        setSubmitting(false);
        if (isMountedRef.current) {
          setSubmitting(false);
        }
      }
    }
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  const handleShowPassword = () => setShowPassword((show) => !show);

  const handleEmail = (e) => {
    formik.setFieldValue('email', e.target.value)
    setEmail(e.target.value)
  };

  const getVerifyButtonLabel = () => {
    if (!enableTwoStep) {
      return "Let's Start!";
    }

    if (useBackupCodeMode) {
      return 'Verify with backup code';
    }

    if (mfaRequired) {
      return 'Verify Authenticator Code';
    }

    return 'Verify OTP';
  };

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        {enableTwoStep && (
          <Button onClick={() => {
            setEnableTwoStep(false);
            setMfaRequired(false);
            setDeviceChecked(false);
            setUseBackupCodeMode(false);
            setBackupCode('');
            setBackupCodeError('');
            setOtpError('');
            formik.setErrors({ afterSubmit: '' });
            setIsEnteredFullOtp(false);
          }}
            startIcon={<ArrowBackIcon />}>Back</Button>
        )}

        <Stack direction="row" alignItems="center" sx={{ mb: 4 }}>
          <Typography
            variant="body1"
            display="inline"
            sx={{
              fontSize: { lg: '2vw', md: '2.5vw', sm: '3vw', xs: '6vw', },
              color: '#212668',
            }}>
            {enableTwoStep ? 'Two-Step Verification' : 'Login'}
          </Typography>
        </Stack>
        {enableTwoStep ? (
          <TwoStepVerification
            setEnterOtp={setEnterOtp}
            email={email || formik.values.email}
            deviceChecked={deviceChecked}
            setDeviceChecked={setDeviceChecked}
            setIsEnteredFullOtp={setIsEnteredFullOtp}
            handleCheckedTrust={handleCheckedTrust}
            mfaRequired={mfaRequired}
            useBackupCodeMode={useBackupCodeMode}
            setUseBackupCodeMode={setUseBackupCodeMode}
            backupCode={backupCode}
            setBackupCode={setBackupCode}
            backupCodeError={backupCodeError}
            setBackupCodeError={setBackupCodeError}
            otpError={otpError}
            setOtpError={setOtpError}
          />
        ) : (
          <Stack spacing={3}>
            {errors.afterSubmit && <Alert severity="error">{errors.afterSubmit}</Alert>}

            <TextField
              fullWidth
              autoComplete="username"
              type="email"
              label="Username"
              placeholder="Username"
              {...getFieldProps('email')}
              onChange={(e) => handleEmail(e)}
              error={Boolean(touched.email && errors.email)}
              helperText={touched.email && errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              autoComplete="password"
              label="Password"
              placeholder="Password"

              type={showPassword ? 'text' : 'password'}
              {...getFieldProps('password')}
              error={Boolean(touched.password && errors.password)}
              helperText={touched.password && errors.password}
              sx={{
                'input::-ms-clear': {
                  display: 'none'
                },
                'input::-ms-reveal': {
                  display: 'none'
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleShowPassword} edge="end">
                      <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Stack direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ my: 2 }}
            >

              <FormControlLabel
                control={<Checkbox {...getFieldProps('remember')} checked={formik.values.remember} />}
                label="Remember me"
              />

              <Link
                component={RouterLink}
                variant="subtitle2"
                to={PATH_AUTH.resetPassword}
                sx={{
                  textDecoration: 'none',
                  fontSize: { lg: '14px', md: '13px', sm: '15px', xs: '11px', xl: '15px' },
                  '&MuiLink-root:hover': {
                    textDecoration: 'none'
                  }
                }}
              >Forgot / Reset password?</Link>
            </Stack>

          </Stack>

        )}


        <>
          {["localhost", "ai.beyondqa.io", "ouc.beyondqa.ai"]?.includes(window.location.hostname) ?
            <Stack alignItems='center' justifyContent='center' sx={{ width: '100%', mt: 2, flexDirection: { sm: 'column', lg: 'row' } }}>
              <Button
                className={!useBackupCodeMode ? 'halloween-animation' : ''}
                fullWidth

                size="large"
                type="submit"
                variant="contained"
                sx={{
                  width: { lg: getVerifyButtonLabel() === "Let's Start!" ? '40%' : '100%', xl: getVerifyButtonLabel() === "Let's Start!" ? '42%' : '100%', qhd: getVerifyButtonLabel() === "Let's Start!" ? '42%' : '100%' },
                  backgroundColor: '#fb5d2e',
                  fontSize: { sm: "12px", qhd: '14px' },
                  // borderRadius: '25px',
                  '&:hover': { backgroundColor: '#FA774F' },
                  // marginTop: '3%',
                  letterSpacing: '1px'
                }}
                disabled={
                  isSubmitting ||
                  (enableTwoStep && mfaRequired && useBackupCodeMode && !backupCode.trim()) ||
                  (enableTwoStep && mfaRequired && !useBackupCodeMode && !isEnteredFullOtp) ||
                  (enableTwoStep && !mfaRequired && !isEnteredFullOtp)
                }
              >
                {getVerifyButtonLabel()}
              </Button>

              {getVerifyButtonLabel() === "Let's Start!" && <Typography variant="body2" align="center" sx={{ fontWeight: "bold", fontSize: { sm: "12px", qhd: '14px' }, color: "#84817a", mx: { sm: 2, qhd: 4 }, my: { sm: 2, lg: 0 } }}>OR</Typography>}

              {getVerifyButtonLabel() === "Let's Start!" && <Button variant='outlined' sx={{ width: { lg: '48%', xl: '48%', qhd: '44%' }, justifyContent: { sm: 'flex-start', xl: 'center' }, fontSize: { sm: "12px", qhd: '14px' } }} onClick={() => window.open(`${process.env.REACT_APP_API_URL}/sso/login/`, "_self")} fullWidth>
                <img src='/static/illustrations/windows_logo_icon.png' alt='win_icon' style={{ width: '38px', height: '38px' }} />&nbsp;Continue with Microsoft
              </Button>}
            </Stack> : <Button
              className={!useBackupCodeMode ? 'halloween-animation' : ''}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: '#fb5d2e',
                borderRadius: '25px',
                '&:hover': { backgroundColor: '#FA774F' },
                marginTop: '3%',
                letterSpacing: '1px'
              }}
              disabled={
                isSubmitting ||
                (enableTwoStep && mfaRequired && useBackupCodeMode && !backupCode.trim()) ||
                (enableTwoStep && mfaRequired && !useBackupCodeMode && !isEnteredFullOtp) ||
                (enableTwoStep && !mfaRequired && !isEnteredFullOtp)
              }
            >
              {getVerifyButtonLabel()}
            </Button>}</>

      </Form>
    </FormikProvider>
  );
}
